import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Cadillac Escalade IQ (linkId 2374).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2374.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the Cadillac Gothic palette/face verbatim from 2384.
 *
 * Pricing: 1.0 ships all-zero pricing for every variant
 * (showPrice:false in 1.0's own presentation config), same as Sierra LD -
 * featureFlags.showPrices is off and specifications.price is left blank
 * rather than showing "Starting from 0 MSRP".
 *
 * Dropped: 1.0's `engine`/`transmission` filterOptions (generic
 * Petrol/Turbo Petrol, CVT/MT/AMT boilerplate found across most GM projects
 * so far, unrelated to any real trim distinction here). `toggleTabs` hides
 * CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2374/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    EscaladeIQ: {
      variants: {
        luxury: {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: '2026',
          configurationId: 'yqnr9w9lnkm8',
          name: {
            en: 'Luxury',
            ar: 'الفئة الفخامة',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: 'AWD',
          trim: 'Luxury',
          highlights: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: 'Torque 1064 NM',
              ar: 'عزم الدوران 1064 نيوتن متر',
            },
            {
              en: 'Range 724 km',
              ar: 'مدى القيادة 724 كم',
            },
          ],
          specs: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: '1064 NM',
              ar: '1064 نيوتن متر',
            },
          ],
          image: 'images/2374/variant/Black_Cherry_Tintcoat.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SC',
          brandItemCode: 'CAESIQ01',
        },
        'premium-luxury': {
          leadModelYear: '2026',
          configurationId: 'svgh4o17nhuj',
          name: {
            en: 'Premium Luxury',
            ar: 'الفئة الفخامة الفاخرة',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: 'AWD',
          trim: 'Premium Luxury',
          highlights: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: 'Torque 1064 NM',
              ar: 'عزم الدوران 1064 نيوتن متر',
            },
            {
              en: 'Range 724 km',
              ar: 'مدى القيادة 724 كم',
            },
          ],
          specs: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: '1064 NM',
              ar: '1064 نيوتن متر',
            },
          ],
          image: 'images/2374/variant/Luna_Metallic.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SD',
          brandItemCode: 'CAESIQ02',
        },
        sport: {
          leadModelYear: '2026',
          configurationId: 'iqznqbapvg2t',
          name: {
            en: 'Sport',
            ar: 'الفئة الرياضية',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: 'AWD',
          trim: 'Sport',
          highlights: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: 'Torque 1064 NM',
              ar: 'عزم الدوران 1064 نيوتن متر',
            },
            {
              en: 'Range 724 km',
              ar: 'مدى القيادة 724 كم',
            },
          ],
          specs: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: '1064 NM',
              ar: '1064 نيوتن متر',
            },
          ],
          image: 'images/2374/variant/Black_Raven.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SF',
          brandItemCode: 'CAESIQ03',
        },
        'premium-sport': {
          leadModelYear: '2026',
          configurationId: '5vlpab0glq63',
          name: {
            en: 'Premium Sport',
            ar: 'فئة الرياضية الفاخرة',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: 'AWD',
          trim: 'Premium Sport',
          highlights: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: 'Torque 1064 NM',
              ar: 'عزم الدوران 1064 نيوتن متر',
            },
            {
              en: 'Range 724 km',
              ar: 'مدى القيادة 724 كم',
            },
          ],
          specs: [
            {
              en: '750 HP',
              ar: '750 حصاناً',
            },
            {
              en: '1064 NM',
              ar: '1064 نيوتن متر',
            },
          ],
          image: 'images/2374/variant/Sandstone.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0.1,
            KSA: 0.1,
            KW: 0.1,
            BHD: 0.1,
            LBP: 0.1,
            OMR: 0.1,
            QAR: 0.1,
            default: 0.1,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SG',
          brandItemCode: 'CAESIQ04',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2374/ext_colors/official/Black_Raven.jpg',
          title: {
            en: 'Landscape 1',
            ar: 'المناظر الطبيعية 1',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2374/HDR_PARKING_LOT.png',
          title: {
            en: 'Landscape 2',
            ar: 'المناظر الطبيعية 2',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          hdylxnq5ongg: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2374/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          z60gu7myefcq: {
            label: {
              en: 'Luna Metallic',
              ar: 'Luna معدني',
            },
            image: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX78',
          },
          pyseql4zovea: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          grtuz3xrjvjx: {
            label: {
              en: 'Black Cherry Tintcoat',
              ar: 'كرزي Black Cherry بطبقتين',
            },
            image: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAB',
            brandItemCode: 'EX58',
          },
          q4qmk6bmi8xz: {
            label: {
              en: 'Deep Space Metallic',
              ar: 'Deep Space معدني',
            },
            image: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAI',
            brandItemCode: 'EX69',
          },
          '2jma8j7wwsg1': {
            label: {
              en: 'Sandstone',
              ar: 'Sandstone',
            },
            image: 'images/2374/ext_colors/official/Sandstone.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Sandstone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX89',
          },
          z0dyjanuh8ez: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2374/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          xoy24l8txfrw: {
            label: {
              en: 'Luna Metallic',
              ar: 'Luna معدني',
            },
            image: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX78',
          },
          ulowkyk9i8l3: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          l33t4kmkku9z: {
            label: {
              en: 'Black Cherry Tintcoat',
              ar: 'كرزي Black Cherry بطبقتين',
            },
            image: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAB',
            brandItemCode: 'EX58',
          },
          zydt6r4wfd05: {
            label: {
              en: 'Deep Space Metallic',
              ar: 'Deep Space معدني',
            },
            image: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAI',
            brandItemCode: 'EX69',
          },
          l1i85v9uhdfv: {
            label: {
              en: 'Sandstone',
              ar: 'Sandstone',
            },
            image: 'images/2374/ext_colors/official/Sandstone.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Sandstone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX89',
          },
          '0ok038kswmfd': {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2374/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          yxw2frp46hgw: {
            label: {
              en: 'Luna Metallic',
              ar: 'Luna معدني',
            },
            image: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX78',
          },
          asxomtrg9w1c: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          '6u9gt5w3ckly': {
            label: {
              en: 'Black Cherry Tintcoat',
              ar: 'كرزي Black Cherry بطبقتين',
            },
            image: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAB',
            brandItemCode: 'EX58',
          },
          yz66c0tiy5od: {
            label: {
              en: 'Deep Space Metallic',
              ar: 'Deep Space معدني',
            },
            image: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAI',
            brandItemCode: 'EX69',
          },
          yrw5q87qr1m2: {
            label: {
              en: 'Sandstone',
              ar: 'Sandstone',
            },
            image: 'images/2374/ext_colors/official/Sandstone.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Sandstone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX89',
          },
          bxh465tzzs6q: {
            label: {
              en: 'Monarch Orange',
              ar: 'برتقالي Monarch',
            },
            image: 'images/2374/ext_colors/official/Monarch_Orange.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Monarch_Orange.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAG',
            brandItemCode: 'EX81',
          },
          '4f9dd3vca75z': {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2374/ext_colors/official/Black_Raven.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          '69xux2hlscm1': {
            label: {
              en: 'Luna Metallic',
              ar: 'Luna معدني',
            },
            image: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Luna_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX78',
          },
          '0pazineb3kjj': {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          qzewltf6e2pf: {
            label: {
              en: 'Black Cherry Tintcoat',
              ar: 'كرزي Black Cherry بطبقتين',
            },
            image: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Black_Cherry_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAB',
            brandItemCode: 'EX58',
          },
          '10xy9i682nxc': {
            label: {
              en: 'Deep Space Metallic',
              ar: 'Deep Space معدني',
            },
            image: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Deep_Space_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAI',
            brandItemCode: 'EX69',
          },
          '37wp0lpjh309': {
            label: {
              en: 'Sandstone',
              ar: 'Sandstone',
            },
            image: 'images/2374/ext_colors/official/Sandstone.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 24,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Sandstone.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G42',
            brandItemCode: 'EX89',
          },
          '37wp0lpjh3099': {
            label: {
              en: 'Monarch Orange',
              ar: 'برتقالي Monarch',
            },
            image: 'images/2374/ext_colors/official/Monarch_Orange.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 25,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2374/ext_colors/official/Monarch_Orange.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAG',
            brandItemCode: 'EX81',
          },
        },
        interior: {
          ia3nfdhs8c03: {
            label: {
              en: 'Sheer Gray with Dark Medium Cinder accents',
              ar: 'Sheer Gray مع لمسات باللون الرمادي الداكن Dark Medium Cinder',
            },
            image: 'images/2374/int_colors/official/Sheer_Gray_Dark_Medium_Cinder.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Sheer_Gray_Dark_Medium_Cinder.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'ENB',
            brandItemCode: 'IN73',
          },
          o9uwhc43s6fh: {
            label: {
              en: 'Backen Black with Santorini accents',
              ar: 'Backen Black مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2374/int_colors/official/Backen_Black_Santorini.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Backen_Black_Santorini.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7D',
            brandItemCode: 'IN38',
          },
          immenmu3osg3: {
            label: {
              en: 'Camelia with Backen Black accents',
              ar: 'Camelia مع لمسات باللون الأسود Backen Black',
            },
            image: 'images/2374/int_colors/official/Camelia_Backen_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Camelia_Backen_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EMY',
            brandItemCode: 'IN40',
          },
          d5qwcahyus4l: {
            label: {
              en: 'Harbor Blue with Backen Black accents',
              ar: 'Harbor Blue مع لمسات باللون الأسود Backen Black',
            },
            image: 'images/2374/int_colors/official/Harbor_Blue_Backen_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Harbor_Blue_Backen_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'ENK',
            brandItemCode: 'IN48',
          },
          lz9ceu2ilwoy: {
            label: {
              en: 'Sheer Gray with Dark Medium Cinder accents',
              ar: 'Sheer Gray مع لمسات باللون الرمادي الداكن Dark Medium Cinder',
            },
            image: 'images/2374/int_colors/official/Sheer_Gray_Dark_Medium_Cinder.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Sheer_Gray_Dark_Medium_Cinder.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'ENB',
            brandItemCode: 'IN73',
          },
          i5bekdchyela: {
            label: {
              en: 'Backen Black with Santorini accents',
              ar: 'Backen Black مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2374/int_colors/official/Backen_Black_Santorini.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Backen_Black_Santorini.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7D',
            brandItemCode: 'IN38',
          },
          '1c0z0wtq7ikc': {
            label: {
              en: 'Camelia with Backen Black accents',
              ar: 'Camelia مع لمسات باللون الأسود Backen Black',
            },
            image: 'images/2374/int_colors/official/Camelia_Backen_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Camelia_Backen_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EMY',
            brandItemCode: 'IN40',
          },
          '0rl5mcxrsw1t': {
            label: {
              en: 'Harbor Blue with Backen Black accents',
              ar: 'Harbor Blue مع لمسات باللون الأسود Backen Black',
            },
            image: 'images/2374/int_colors/official/Harbor_Blue_Backen_Black.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2374/int_colors/official/Harbor_Blue_Backen_Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'ENK',
            brandItemCode: 'IN48',
          },
        },
      },
      features: {
        '5pgoz7t60v6h': {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2374/features/lux/Exterior_Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'Bold new design',
            ar: 'تصميم جديد جريء',
          },
          type: 'exterior',
        },
        '6jwn13j18qd6': {
          label: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          image: 'images/2374/features/lux/Front grille.jpg',
          title: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          desc: {
            en: 'Galvano finished grille with unique illuminated grille patterns',
            ar: 'شبك أمامي بتشطيب Galvano مع أنماط إضاءة فريدة',
          },
          type: 'exterior',
        },
        s3bl9flf84yy: {
          label: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          image: 'images/2374/features/lux/Wheels.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          desc: {
            en: '24" 7-spoke alloy wheels with mask and polish with machine face and  high-gloss Black pockets and inserts',
            ar: 'عجلات معدنية قياس 24 بوصة بتصميم ذي 7 أذرع مع تشطيب Mask and Polish، وواجهة مشغولة آلياً، وجيوب وتطعيمات باللون الأسود شديد اللمعان',
          },
          type: 'exterior',
        },
        z7f5dg1363j7: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2374/features/lux/Info_screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        gilc1wkl093x: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2374/features/lux/Sound_System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '21-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ21 مكبر صوت',
          },
          type: 'interior',
        },
        k89m8cufzlmf: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2374/features/lux/Transmisson_Screen.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        k9kcgg3ekptk: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2374/features/lux/Sound_System.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        limkmbko6ukv: {
          label: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          image: 'images/2374/features/lux/AC_Vents.jpg',
          title: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          desc: {
            en: '5 - Zone automatic climate control',
            ar: 'نظام تحكم تلقائي بالمناخ بخمس مناطق',
          },
          type: 'interior',
        },
        palz11dc7i8g: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2374/plux/Exterior Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'Bold new design',
            ar: 'تصميم جديد جريء',
          },
          type: 'exterior',
        },
        hvl07w5yuc7w: {
          label: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          image: 'images/2374/plux/Front grille.jpg',
          title: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          desc: {
            en: 'Galvano finished grille with unique illuminated grille patterns',
            ar: 'شبك أمامي بتشطيب Galvano مع أنماط إضاءة فريدة',
          },
          type: 'exterior',
        },
        uz3ja22iudrt: {
          label: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          image: 'images/2374/plux/Wheels.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          desc: {
            en: '24" 7-spoke alloy wheels with mask and polish with machine face and  high-gloss Black pockets and inserts',
            ar: 'عجلات معدنية قياس 24 بوصة بتصميم ذي 7 أذرع مع تشطيب Mask and Polish، وواجهة مشغولة آلياً، وجيوب وتطعيمات باللون الأسود شديد اللمعان',
          },
          type: 'exterior',
        },
        k47zzp6rkwct: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2374/plux/Infotainment screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        br1o4haus32j: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2374/plux/Sound System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        gy96py28zcyg: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2374/plux/Transmisson Screen.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        g3pasrhugsxy: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2374/plux/Ambient Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        aohmqq9vt0vr: {
          label: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          image: 'images/2374/plux/AC Vents.jpg',
          title: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          desc: {
            en: '5 - Zone automatic climate control',
            ar: 'نظام تحكم تلقائي بالمناخ بخمس مناطق',
          },
          type: 'interior',
        },
        r3smobkiwrrx: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2374/spo/Exterior Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'Bold new design',
            ar: 'تصميم جديد جريء',
          },
          type: 'exterior',
        },
        '0jwb6s4t5liv': {
          label: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          image: 'images/2374/spo/Front grille.jpg',
          title: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          desc: {
            en: 'Obsidian Chrome finished grille with unique illuminated grille patterns',
            ar: 'شبك أمامي بتشطيب Obsidian Chrome مع أنماط إضاءة فريدة',
          },
          type: 'exterior',
        },
        fpwix2qmqkw4: {
          label: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          image: 'images/2374/spo/Wheels.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          desc: {
            en: '24" 7-spoke alloy wheels with diamond cut face with high gloss Black pockets and inserts and dark tintcoat',
            ar: 'عجلات معدنية قياس 24 بوصة بتصميم ذي 7 أذرع، مع واجهة Diamond Cut، وجيوب وتطعيمات باللون الأسود شديد اللمعان، وتشطيب Dark Tintcoat',
          },
          type: 'exterior',
        },
        e9qp6nnv5f19: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2374/spo/Infotainment screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        wsz8et9j4val: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2374/spo/Sound System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '21-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ21 مكبر صوت',
          },
          type: 'interior',
        },
        qg5s843osgr8: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2374/spo/Transmisson Screen.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        e91e6sdviyij: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2374/spo/Sound System.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        wwgrqr1m9kn4: {
          label: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          image: 'images/2374/spo/AC Vents.jpg',
          title: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          desc: {
            en: '5 - Zone automatic climate control',
            ar: 'نظام تحكم تلقائي بالمناخ بخمس مناطق',
          },
          type: 'interior',
        },
        '0gvs8s0ln4fh': {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2374/pspo/Exterior Design.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'Bold new design',
            ar: 'تصميم جديد جريء',
          },
          type: 'exterior',
        },
        nb8tkmndj3vg: {
          label: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          image: 'images/2374/pspo/Front grille.jpg',
          title: {
            en: 'Front grille',
            ar: 'الشبك الأمامي',
          },
          desc: {
            en: 'Obsidian Chrome finished grille with unique illuminated grille patterns',
            ar: 'شبك أمامي بتشطيب Obsidian Chrome مع أنماط إضاءة فريدة',
          },
          type: 'exterior',
        },
        m37v8o8yl5x2: {
          label: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          image: 'images/2374/pspo/Wheels.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'العجلات المعدنية',
          },
          desc: {
            en: '24" 7-spoke alloy wheels with diamond cut face with high gloss Black pockets and inserts and dark tintcoat',
            ar: 'عجلات معدنية قياس 24 بوصة بتصميم ذي 7 أذرع، مع واجهة Diamond Cut، وجيوب وتطعيمات باللون الأسود شديد اللمعان، وتشطيب Dark Tintcoat',
          },
          type: 'exterior',
        },
        xogfbqwjubb0: {
          label: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2374/pspo/Infotainment screen.jpg',
          title: {
            en: 'Infotainment screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: 'Curved pillar-to-pillar 55" total diagonal display',
            ar: 'شاشة منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
          },
          type: 'interior',
        },
        s4f98dj7g3mk: {
          label: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2374/pspo/Sound System.jpg',
          title: {
            en: 'Sound System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: '38-speaker AKG Studio Audio System',
            ar: 'نظام AKG Studio الصوتي المزوّد بـ38 مكبر صوت',
          },
          type: 'interior',
        },
        epg4m463go0z: {
          label: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          image: 'images/2374/pspo/Transmisson Screen.jpg',
          title: {
            en: 'Command Center',
            ar: 'مركز التحكم',
          },
          desc: {
            en: 'High-Tech Front Command Center',
            ar: 'مركز التحكم الأمامي عالي التقنية',
          },
          type: 'interior',
        },
        tvh5xpuhzsbz: {
          label: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          image: 'images/2374/pspo/Ambient Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'الإضاءة المحيطية',
          },
          desc: {
            en: 'LED ambient lighting with 126 colours customization',
            ar: 'إضاءة LED محيطية مع إمكانية تخصيص 126 لوناً',
          },
          type: 'interior',
        },
        ngzrs5lwkgop: {
          label: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          image: 'images/2374/pspo/AC Vents.jpg',
          title: {
            en: 'Auto Climate Control',
            ar: 'نظام التحكم التلقائي بالمناخ',
          },
          desc: {
            en: '5 - Zone automatic climate control',
            ar: 'نظام تحكم تلقائي بالمناخ بخمس مناطق',
          },
          type: 'interior',
        },
      },
      accessories: {
        gffn6s182da2: {
          label: {
            en: 'WO Seat Display',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
        },
        f2a1kj98at78: {
          label: {
            en: 'Rear-seat entertainment system',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: 'images/2374/features/lux/W_Seat_Display_Sheer_Gray.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC206',
        },
        m639a7vzbuw2: {
          label: {
            en: 'Roof',
            ar: 'السقف',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        fbyp03faxepb: {
          label: {
            en: 'Black Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2374/features/lux/CAMERA_ROOF.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
        gnrq06v7wp8x: {
          label: {
            en: 'Executive pack WO',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ABV',
        },
        u3aqo2hqpc6a: {
          label: {
            en: 'Executive 2nd row seating',
            ar: 'المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2374/plux/W_Executive_Pack_Harbor_Blue.jpg',
          category: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'AL4',
          brandItemCode: 'AC173',
        },
        '4pgvkggs0u3k': {
          label: {
            en: 'Roof',
            ar: 'السقف',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        dbbvufiql40h: {
          label: {
            en: 'Black roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2374/plux/CAMERA_ROOF.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
        az7g6geux1c1: {
          label: {
            en: 'WO Seat Display',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
        },
        '9b2d90cotnka': {
          label: {
            en: 'Rear-seat entertainment system',
            ar: 'نظام الترفيه للمقاعد الخلفية',
          },
          image: 'images/2374/spo/W_Seat_Display_Sheer_Gray.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC206',
        },
        '5xqqme5zq02y': {
          label: {
            en: 'Roof',
            ar: 'السقف',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        gorwm4cieo58: {
          label: {
            en: 'Black roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2374/spo/CAMERA_ROOF.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
        wtnujr9hlztg: {
          label: {
            en: 'Executive pack WO',
            ar: 'باقة المقاعد التنفيذية للصف الثاني',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'ABV',
        },
        az7g6geux1c2: {
          label: {
            en: 'Executive 2nd row seating',
            ar: 'المقاعد التنفيذية للصف الثاني',
          },
          image: 'images/2374/pspo/W_Executive_Pack_Harbor_Blue.jpg',
          category: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          price: {
            KSA: 125,
            UAE: 123,
            OMR: 13,
            BHD: 13,
            QAR: 122,
            JOD: 24,
            KW: 10,
            LBP: 33,
            IQD: 33,
            default: 123,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'AL4',
          brandItemCode: 'AC173',
        },
        quf319yxtxl8: {
          label: {
            en: 'Roof',
            ar: 'السقف',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        j2oqomprlsyv: {
          label: {
            en: 'Black roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2374/pspo/CAMERA_ROOF.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
      },
      packages: {},
      accessoryCategories: {
        interior: ['Seats'],
        options: ['Options & Accessories'],
      },
      interactions: {
        ihmz2ch7lru2: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '53ehrrkf0yxy': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        wn7m7l9dpwjh: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        zhd8ypjsjml8: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        qwf8jkzszwpg: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        n04pf5yhy6ml: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        pdvesjvy4q3c: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        tipo90ysous9: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '9qigkas694ke': {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        zy0w7fsjm17p: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        k8yumyg89ea4: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '004q0jbdcutj': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        nsrbl8k7p41m: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        q5i2b8dp8qiv: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        ca4j5ldx7rcv: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        '0fbm80b2gwrp': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        cuk1rase0zxe: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        pyau4ud8ozn6: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        f6mckp6y4528: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        tbur04pwvnag: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        lpgfndx8gmly: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        bkr8amy2tvbs: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        '6146uzbd6kui': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        iq7p37hq4g0j: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني',
          },
        },
        interiorCamera_2: {
          title: {
            en: 'Third Row Seat',
            ar: 'مقعد الصف الثالث',
          },
        },
      },
      name: {
        en: 'Cadillac Escalade IQ',
        ar: 'كاديلاك إسكاليد IQ',
      },
      specifications: {
        heading: {
          en: 'Cadillac Escalade IQ',
          ar: 'كاديلاك إسكاليد IQ',
        },
        trim: {
          en: 'Luxury',
          ar: 'Luxury',
        },
        price: {
          en: '',
          ar: '',
        },
        model: 'Cadillac Escalade IQ',
        seatingCapacity: '7 Seats',
        dimensions: {
          length: '5697 mm',
          width: '',
          height: '1st Row - 1105mm, 2nd Row -1013mm and 3rd Row - 945mm',
          wheelbase: '3459 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '750 HP',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '1064 NM',
            unit: '',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '724 km',
            unit: '',
            label: {
              en: 'Range',
              ar: 'مدى القيادة',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'CADILLAC ESCALADE IQ',
              ar: 'كاديلاك ESCALADE IQ',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Intuitive Infotainment',
              ar: 'نظام معلومات وترفيه متطور',
            },
            subtitle: {
              en: 'Curved pillar-to-pillar 55” total diagonal LED display',
              ar: 'شاشة LED منحنية تمتد من عمود إلى عمود بقياس إجمالي 55 بوصة',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Black Raven, Luna Metallic, Vibrant White Tricoat, Black Cherry Tintcoat, Deep Space Metallic, Sandstone',
              ar: 'أسود Raven، Luna معدني، أبيض Vibrant بثلاث طبقات، كرزي Black Cherry بطبقتين، Deep Space معدني، Sandstone',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الألوان الداخلية',
            },
            subtitle: {
              en: 'Sheer Gray with Dark Medium Cinder accents, Backen Black with Santorini accents, Camelia with Backen Black accents, Harbor Blue with Backen Black accents',
              ar: 'Sheer Gray مع لمسات باللون الرمادي الداكن Dark Cinder، Backen Black مع لمسات باللون الأزرق Santorini، Camelia مع لمسات باللون الأسود Backen Black، Harbor Blue مع لمسات باللون الأسود Backen Black',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Energy',
              ar: 'الطاقة',
            },
            subtitle: {
              en: '22 Kwh standard charging',
              ar: 'شحن قياسي بقدرة 22 كيلوواط',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3459 mm',
              ar: '3459 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Total Length',
              ar: 'الطول الإجمالي',
            },
            subtitle: {
              en: '5697 mm',
              ar: '5697 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Headroom',
              ar: 'حيّز الرأس',
            },
            subtitle: {
              en: '1st Row - 1105mm, 2nd Row -1013mm and 3rd Row - 945mm',
              ar: 'الصف الأول: 1105 مم، الصف الثاني: 1013 مم، الصف الثالث: 945 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Legroom',
              ar: 'حيّز الأرجل',
            },
            subtitle: {
              en: '1st Row - 1148m, 2nd Row -1049mm and 3rd Row - 820mm',
              ar: 'الصف الأول: 1148 مم، الصف الثاني: 1049 مم، الصف الثالث: 820 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo volume',
              ar: 'حجم الحمولة',
            },
            subtitle: {
              en: '1014 liters (668 liters behind third row + 345 liters eTrunkTM)',
              ar: '1014 لتراً (668 لتراً خلف الصف الثالث + 345 لتراً في eTrunk™)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'نظام الدفع',
            },
            subtitle: {
              en: 'Dual Motor AWD',
              ar: 'نظام دفع رباعي بمحركين كهربائيين',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد المقاعد',
            },
            subtitle: {
              en: '7 Seats',
              ar: '7 مقاعد',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.cadillacarabia.com/ae-en/privacy-statement',
        ar: 'https://www.cadillacarabia.com/ae-ar/privacy-statement',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Cadillac Escalade IQ configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my Cadillac Escalade IQ configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My Cadillac Escalade IQ Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom Cadillac Escalade IQ configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my Cadillac Escalade IQ Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream Cadillac Escalade IQ. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: '',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: '',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: '',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: '',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: '',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
      {
        id: 5,
        country: 'KSA',
        value: '',
        dealerId: '318928',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - King Fahad Road, Dammam',
          ar: 'الجميح للسيارات - طريق الملك فهد، الدمام',
        },
      },
      {
        id: 6,
        country: 'KSA',
        value: '',
        dealerId: '213142',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Rawdah Khurais Road, Riyadh',
          ar: 'الجميح للسيارات - طريق الروضة خريص، الرياض',
        },
      },
      {
        id: 7,
        country: 'KSA',
        value: '',
        dealerId: '307267',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Malibari Al Madina Road, Jeddah',
          ar: 'الجميح للسيارات - طريق ماليباري المدينة، جدة',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: '',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: '',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '259952',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    showPrices: false,
    showDisplayCarPrice: false,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'escalade iq',
    // Exact model name for the lead file's Model column.
    leadModelName: 'Escalade IQ',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'EscaladeIQ',
    modelName: 'EscaladeIQ',
    pageTitle: 'Escalade IQ Configurator',
    client: 'GM',
    brand: 'cadillac',
    modelId: 'CAESC_IQ',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'cadillac',
      modelName: 'escaladeiq',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2374/presentation_video_1.webm',
      loadingImage: 'images/2374/sliderImage.webp',
      transitionImage: 'images/2374/transition_logo.png',
      headerLogo: 'images/2374/transition_logo.png',
      headerLogoKeepColor: true,
      headerLogoInvertInLight: true,
      // Cadillac's crest reads too small at the shared default header-logo size.
      headerLogoLarge: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '750 HP',
            ar: '750 حصاناً',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '1064 NM',
            ar: '1064 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Range',
            ar: 'مدى القيادة',
          },
          value: {
            en: '724 km',
            ar: '724 كم',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Cadillac Gothic\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Cadillac Gothic\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#1a1971',
      '--brand-primary-hover': '#15146a',
      '--brand-secondary': '#191897',
      '--brand-accent': '#1a1971',
      '--brand-accent-hover': '#15146a',
      '--brand-accent-light': '#e8e8f0',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#1a1971',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#1a1971',
      '--brand-toggle-gradient-to': '#191897',
      '--brand-toggle-knob-border': '#1a1971',
      '--brand-accent-secondary': '#1a1971',
      '--brand-card-radius': '0px',
      '--brand-card-radius-sm': '0px',
      '--brand-card-radius-lg': '0px',
      '--brand-button-radius': '0px',
      '--brand-button-radius-sm': '0px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '0px',
      // Enter-CTA fill: white with the Cadillac blue chevron, rather than
      // the default #0072ce block. Shadow keeps the label legible on it.
      '--brand-cta-arrow-bg': '#ffffff',
      '--brand-cta-arrow-fg': '#1a1971',
      '--brand-cta-shimmer': '#ffffff',
      '--enter-configurator-text-shadow': '0px 0px 12px #000000',
      '--brand-button-border': '#ffffff',
      '--brand-dialog-radius': '0px',
      '--brand-dialog-option-radius': '0px',
    },
  },
} as unknown as UIMetadataComplete;
